import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Mail, Instagram, Facebook, Linkedin, Youtube } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  LANDING_PAGE_CONFIGS,
  LANDING_PAGE_FOOTER_ORDER,
  LANDING_PAGE_FOOTER_LABELS,
  LANDING_PAGE_THEME_FOOTER_ORDER,
  LANDING_PAGE_THEME_FOOTER_LABELS,
} from '../pages/landingPageConfigs';
import { HOI_BUUR_SOCIALS } from '../lib/constants';
import { openCookieSettings } from './CookieConsent';

// TikTok is not part of lucide-react's icon set; render a compact inline SVG
// so we don't pull in an extra dependency for one glyph.
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.6 6.7c-1.3-.8-2.1-2.2-2.2-3.7h-3v13.7c0 1.5-1.2 2.8-2.8 2.8s-2.8-1.3-2.8-2.8 1.2-2.8 2.8-2.8c.3 0 .6.1.9.2v-3c-.3 0-.6-.1-.9-.1-3.2 0-5.8 2.6-5.8 5.7s2.6 5.7 5.8 5.7 5.8-2.5 5.8-5.7V9.2c1.2.8 2.7 1.3 4.3 1.3v-3c-.9.1-1.7-.1-2.1-.8z" />
  </svg>
);

const SOCIAL_ITEMS = [
  { key: 'instagram', Icon: Instagram, label: 'Instagram' },
  { key: 'facebook', Icon: Facebook, label: 'Facebook' },
  { key: 'linkedin', Icon: Linkedin, label: 'LinkedIn' },
  { key: 'tiktok', Icon: TikTokIcon, label: 'TikTok' },
  { key: 'youtube', Icon: Youtube, label: 'YouTube' },
];

const Footer = () => {
  const { t } = useAuth();
  const activeSocials = SOCIAL_ITEMS.filter(({ key }) => HOI_BUUR_SOCIALS[key]);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || subscribing) return;
    setSubscribing(true);
    try {
      const r = await api.post('/newsletter/subscribe', { email: email.trim(), wijken: [], interests: [] });
      if (r.data.status === 'already_subscribed') {
        toast.info('Je bent al aangemeld voor de buurt-update');
      } else {
        toast.success('Bijna klaar! Check je e-mail om je aanmelding te bevestigen');
      }
      setEmail('');
    } catch {
      toast.error('Aanmelden is niet gelukt, probeer het later nog eens');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-hoi-blue text-white py-8 mt-16 overflow-x-hidden" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center gap-6">
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <Heart className="w-5 h-5 text-hoi-coral fill-hoi-coral flex-shrink-0" aria-hidden="true" />
            <span className="text-sm text-white/80">{t('footer.initiative')}</span>
            {/* PGA logo */}
            <img src="/images/logo-pga.png" alt="Positief Gezond Almere" className="h-8 ml-1" data-testid="footer-pga-logo" />
          </div>

          {/* Nieuwsbrief-inschrijving (wekelijkse buurt-update) */}
          <form onSubmit={subscribe} className="w-full max-w-md text-center" data-testid="footer-newsletter">
            <p className="text-sm font-semibold text-white/90">Wekelijkse buurt-update</p>
            <p className="text-xs text-white/60 mb-2">Elke donderdag de leukste activiteiten bij jou in de buurt.</p>
            <div className="flex items-center gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder="jouw@email.nl"
                data-testid="footer-newsletter-email"
                className="flex-1 rounded-full px-4 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-hoi-coral"
              />
              <button
                type="submit"
                disabled={subscribing}
                data-testid="footer-newsletter-submit"
                className="bg-hoi-coral hover:bg-hoi-coral/90 text-white rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50 transition-colors whitespace-nowrap"
              >
                {subscribing ? '…' : 'Aanmelden'}
              </button>
            </div>
          </form>

          <div className="flex items-center gap-4 sm:gap-6 text-sm flex-wrap justify-center">
            <Link to="/verhalen" className="text-white/80 hover:text-white transition-colors" data-testid="footer-news-link">{t('nav.news')}</Link>
            <Link to="/discover" className="text-white/80 hover:text-white transition-colors" data-testid="footer-discover-link">{t('nav.discover')}</Link>
            <Link to="/poster" className="text-white/80 hover:text-white transition-colors" data-testid="footer-poster-link">Poster maken</Link>
            <Link to="/contact" className="text-white/80 hover:text-white transition-colors flex items-center gap-1" data-testid="footer-contact-link">
              <Mail className="w-3.5 h-3.5" aria-hidden="true" />
              {t('footer.contact')}
            </Link>
            <a href="https://positiefgezondalmere.nl" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors">
              Positief Gezond Almere
            </a>
          </div>
          {/* Juridische en privacy links */}
          <div className="flex items-center gap-4 sm:gap-6 text-xs flex-wrap justify-center text-white/70">
            <Link to="/privacyverklaring" className="hover:text-white transition-colors" data-testid="footer-privacy-link">
              Privacyverklaring
            </Link>
            <Link to="/cookies" className="hover:text-white transition-colors" data-testid="footer-cookies-link">
              Cookieverklaring
            </Link>
            <button
              onClick={openCookieSettings}
              className="hover:text-white transition-colors underline-offset-2 hover:underline"
              data-testid="footer-cookie-settings-btn"
            >
              Cookievoorkeuren
            </button>
          </div>
          {/* Social icons — only render platforms with a configured URL */}
          {activeSocials.length > 0 && (
            <div className="flex flex-col items-center gap-3 pt-2 border-t border-white/10 w-full max-w-md" data-testid="footer-socials">
              <span className="text-xs uppercase tracking-wider text-white/60">Volg ons</span>
              <div className="flex items-center gap-3">
                {activeSocials.map(({ key, Icon, label }) => (
                  <a
                    key={key}
                    href={HOI_BUUR_SOCIALS[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${label} van Hoi Buur Almere`}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white/90 hover:bg-hoi-coral hover:text-white transition-all duration-200 hover:scale-105"
                    data-testid={`footer-social-${key}`}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          )}
          {/* Activiteiten per wijk — SEO-landingspagina's */}
          <nav aria-label="Activiteiten per wijk" className="flex flex-col items-center gap-2 pt-2 border-t border-white/10 w-full max-w-md">
            <span className="text-xs uppercase tracking-wider text-white/60">Activiteiten per wijk</span>
            <div className="flex items-center gap-4 flex-wrap justify-center text-sm">
              {LANDING_PAGE_FOOTER_ORDER.map((key) => {
                const cfg = LANDING_PAGE_CONFIGS[key];
                if (!cfg) return null;
                return (
                  <Link
                    key={key}
                    to={cfg.slug}
                    className="text-white/90 hover:text-white hover:underline underline-offset-4 transition-colors"
                    data-testid={`footer-landing-${key}`}
                  >
                    {LANDING_PAGE_FOOTER_LABELS[key] || cfg.heading}
                  </Link>
                );
              })}
            </div>
          </nav>
          {/* Populaire onderwerpen — thema-landingspagina's (Fase 2) */}
          <nav aria-label="Populaire onderwerpen" className="flex flex-col items-center gap-2 pt-2 border-t border-white/10 w-full max-w-2xl">
            <span className="text-xs uppercase tracking-wider text-white/60">Populaire onderwerpen</span>
            <div className="flex items-center gap-x-4 gap-y-2 flex-wrap justify-center text-sm">
              {LANDING_PAGE_THEME_FOOTER_ORDER.map((key) => {
                const cfg = LANDING_PAGE_CONFIGS[key];
                if (!cfg) return null;
                return (
                  <Link
                    key={key}
                    to={cfg.slug}
                    className="text-white/90 hover:text-white hover:underline underline-offset-4 transition-colors"
                    data-testid={`footer-landing-${key}`}
                  >
                    {LANDING_PAGE_THEME_FOOTER_LABELS[key] || cfg.heading}
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
